import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Empty,
  Input,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Tag,
  Upload,
  message,
} from "antd";
import {
  PlusOutlined,
  UploadOutlined,
  WhatsAppOutlined,
  SendOutlined,
  ArrowLeftOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const API_BASE = "https://api-ctpm6rtdyq-uc.a.run.app";

const VARIABLES = [
  { label: "Nome", value: "{{NOME}}" },
  { label: "Primeiro Nome", value: "{{PRIMEIRO_NOME}}" },
  { label: "Cidade", value: "{{CIDADE}}" },
  { label: "Cupom", value: "{{CUPOM}}" },
];

function normalizePhone(phone) {
  if (!phone) return null;
  let number = String(phone).replace(/\D/g, "");
  if (number.startsWith("00")) number = number.slice(2);
  if (!number.startsWith("55")) number = "55" + number;
  if (number.length < 12 || number.length > 13) return null;
  return number;
}

function firstName(name) {
  return String(name || "").trim().split(" ").filter(Boolean)[0] || "";
}

function formatWhatsAppText(text) {
  return String(text || "")
    .replace(/\r\n/g, "\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function renderPreview(template, contact = {}) {
  const nome = contact.nome || contact.name || "";
  return formatWhatsAppText(template)
    .replace(/\{\{NOME\}\}/gi, nome)
    .replace(/\{\{PRIMEIRO_NOME\}\}/gi, firstName(nome))
    .replace(/\{\{CIDADE\}\}/gi, contact.cidade || contact.city || "")
    .replace(/\{\{CUPOM\}\}/gi, contact.cupom || contact.coupon || "");
}

function percent(value, total) {
  if (!total) return 0;
  return Math.min(100, Math.round((Number(value || 0) / Number(total || 0)) * 100));
}

function statusColor(status) {
  if (status === "PROCESSING") return "green";
  if (status === "DONE") return "blue";
  if (status === "PAUSED") return "orange";
  if (status === "DRAFT") return "default";
  return "default";
}

function statusLabel(status) {
  return {
    PROCESSING: "Processando",
    DONE: "Finalizada",
    PAUSED: "Pausada",
    DRAFT: "Rascunho",
    CANCELED: "Cancelada",
  }[status] || status;
}

function insertAtCursor(text, insert, ref) {
  const el = ref.current?.resizableTextArea?.textArea;
  if (!el) return `${text}${insert}`;

  const start = el.selectionStart;
  const end = el.selectionEnd;
  const next = text.slice(0, start) + insert + text.slice(end);

  setTimeout(() => {
    el.focus();
    el.selectionStart = el.selectionEnd = start + insert.length;
  }, 0);

  return next;
}

export default function MassBroadcast() {
  const messageRef = useRef(null);

  const [mode, setMode] = useState("dashboard");
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const [name, setName] = useState("");
  const [text, setText] = useState(`Olá {{PRIMEIRO_NOME}}, tudo bem?

Última chance!

Seu carrinho ainda está te esperando.

🔥 Só HOJE:
Frete grátis + R$15 OFF

Use o cupom FINALIZAR15

👉 Finalize aqui:`);

  const [startNow, setStartNow] = useState(true);
  const [speed, setSpeed] = useState("normal");
  const [contacts, setContacts] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);

  async function loadCampaigns() {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/broadcasts?ts=${Date.now()}`);
      const data = await res.json();

      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Erro ao carregar campanhas");
      }

      setCampaigns(data.campaigns || []);
    } catch (e) {
      message.error(e.message || "Erro ao carregar campanhas");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadCampaigns();
    const interval = setInterval(loadCampaigns, 3000);
    return () => clearInterval(interval);
  }, []);

  const parsed = useMemo(() => {
    const seen = new Set();
    const valid = [];
    const invalid = [];
    const duplicated = [];

    contacts.forEach((contact, index) => {
      const rawPhone =
        contact.phone ||
        contact.telefone ||
        contact.numero ||
        contact.whatsapp ||
        contact.celular;

      const phone = normalizePhone(rawPhone);

      if (!phone) {
        invalid.push({ ...contact, index, rawPhone, status: "INVALID" });
        return;
      }

      if (seen.has(phone)) {
        duplicated.push({ ...contact, index, rawPhone, phone, status: "DUPLICATED" });
        return;
      }

      seen.add(phone);

      valid.push({
        ...contact,
        index,
        rawPhone,
        phone,
        status: "VALID",
        previewMessage: renderPreview(text, contact),
      });
    });

    return { valid, invalid, duplicated };
  }, [contacts, text]);

  const totals = useMemo(() => {
    const sent = campaigns.reduce((acc, c) => acc + Number(c.sentCount || 0), 0);
    const pending = campaigns.reduce((acc, c) => acc + Number(c.pendingCount || 0), 0);
    const failed = campaigns.reduce((acc, c) => acc + Number(c.failedCount || 0), 0);
    const claimed = campaigns.reduce((acc, c) => acc + Number(c.claimedCount || 0), 0);

    return {
      sent,
      pending,
      failed,
      claimed,
      successRate: sent + failed > 0 ? ((sent / (sent + failed)) * 100).toFixed(1) : "0.0",
    };
  }, [campaigns]);

  const chartData = campaigns.slice(0, 8).map((item) => ({
    name: item.name || "Campanha",
    enviados: item.sentCount || 0,
    falhas: item.failedCount || 0,
    pendentes: item.pendingCount || 0,
  }));

  const validationPie = [
    { name: "Válidos", value: parsed.valid.length },
    { name: "Inválidos", value: parsed.invalid.length },
    { name: "Duplicados", value: parsed.duplicated.length },
  ];

  async function handleUpload(file) {
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer);
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      setContacts(rows);
      setPreviewIndex(0);
      message.success(`${rows.length} linhas importadas`);
    } catch (e) {
      message.error("Erro ao ler Excel");
    }

    return false;
  }

  function insertText(value) {
    setText((prev) => insertAtCursor(prev, value, messageRef));
  }

  async function handleCreate() {
    try {
      const finalMessage = formatWhatsAppText(text);

      if (!name.trim()) return message.error("Digite o nome da campanha");
      if (!finalMessage) return message.error("Digite a mensagem");
      if (!contacts.length) return message.error("Suba uma planilha com os números");
      if (!parsed.valid.length) return message.error("Nenhum número válido encontrado");

      setCreating(true);

      const res = await fetch(`${API_BASE}/broadcasts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          message: finalMessage,
          contacts,
          startNow,
          speed,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Erro ao criar campanha");
      }

      message.success("Campanha criada com sucesso ✅");

      setName("");
      setText(`Olá {{PRIMEIRO_NOME}}, tudo bem?

Última chance!

Seu carrinho ainda está te esperando.

🔥 Só HOJE:
Frete grátis + R$15 OFF

Use o cupom FINALIZAR15

👉 Finalize aqui:`);
      setContacts([]);
      setStartNow(true);
      setSpeed("normal");
      setMode("dashboard");

      await loadCampaigns();
    } catch (e) {
      message.error(e.message || "Erro ao criar campanha");
    } finally {
      setCreating(false);
    }
  }

  const previewContact = parsed.valid[previewIndex] || parsed.valid[0] || {};

  if (mode === "create") {
    return (
      <div style={styles.page}>
        <Space style={styles.topBar}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => setMode("dashboard")}>
            Voltar
          </Button>

          <div>
            <h1 style={styles.title}>Nova campanha</h1>
            <p style={styles.subtitle}>
              Crie uma campanha com variáveis, Excel e preview em tempo real.
            </p>
          </div>
        </Space>

        <Row gutter={[18, 18]}>
          <Col xs={24} lg={14}>
            <Card style={styles.card}>
              <Space direction="vertical" size={16} style={{ width: "100%" }}>
                <div>
                  <label style={styles.label}>Nome da campanha</label>
                  <Input
                    size="large"
                    placeholder="Ex: Promoção Copa - Clientes VIP"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>

                <div>
                  <label style={styles.label}>Mensagem</label>
                  <Input.TextArea
                    ref={messageRef}
                    rows={12}
                    placeholder={`Ex:
Olá {{PRIMEIRO_NOME}}, tudo bem?

Última chance!

Seu carrinho ainda está te esperando.

🔥 Só HOJE:
Frete grátis + R$15 OFF

Use o cupom FINALIZAR15

👉 Finalize aqui:`}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                  />
                  <p style={styles.hint}>
                    As quebras de linha daqui serão enviadas exatamente assim no WhatsApp.
                  </p>
                </div>

                <div>
                  <label style={styles.label}>Variáveis rápidas</label>
                  <Space wrap>
                    {VARIABLES.map((item) => (
                      <Button key={item.value} size="small" onClick={() => insertText(item.value)}>
                        + {item.label}
                      </Button>
                    ))}

                    <Button size="small" onClick={() => insertText("\n\n")}>
                      + Quebra de linha
                    </Button>

                    <Button size="small" onClick={() => insertText("🔥 ")}>
                      + 🔥
                    </Button>

                    <Button size="small" onClick={() => insertText("👉 ")}>
                      + 👉
                    </Button>

                    <Button size="small" onClick={() => setText(formatWhatsAppText(text))}>
                      Limpar espaços
                    </Button>
                  </Space>
                </div>

                <div>
                  <label style={styles.label}>Lista de contatos</label>
                  <Upload beforeUpload={handleUpload} maxCount={1} accept=".xlsx,.xls,.csv">
                    <Button icon={<UploadOutlined />} size="large">
                      Subir Excel
                    </Button>
                  </Upload>

                  <p style={styles.hint}>
                    Colunas aceitas: telefone, numero, whatsapp, celular, nome, cidade e cupom.
                  </p>
                </div>

                <Row gutter={[12, 12]}>
                  <Col xs={24} md={12}>
                    <label style={styles.label}>Velocidade do n8n</label>
                    <Select
                      size="large"
                      value={speed}
                      onChange={setSpeed}
                      style={{ width: "100%" }}
                      options={[
                        { label: "Lenta — menor volume", value: "slow" },
                        { label: "Normal — recomendado", value: "normal" },
                        { label: "Rápida — listas quentes", value: "fast" },
                      ]}
                    />
                  </Col>

                  <Col xs={24} md={12}>
                    <label style={styles.label}>Início</label>
                    <Card size="small">
                      <Checkbox checked={startNow} onChange={(e) => setStartNow(e.target.checked)}>
                        Começar imediatamente
                      </Checkbox>
                    </Card>
                  </Col>
                </Row>

                <Button
                  type="primary"
                  size="large"
                  icon={<SendOutlined />}
                  loading={creating}
                  onClick={handleCreate}
                  block
                >
                  Criar campanha
                </Button>
              </Space>
            </Card>
          </Col>

          <Col xs={24} lg={10}>
            <Space direction="vertical" size={18} style={{ width: "100%" }}>
              <Card style={styles.card} title="Preview WhatsApp">
                <div style={styles.phoneMock}>
                  <div style={styles.phoneHeader}>
                    <WhatsAppOutlined /> Prévia da mensagem
                  </div>

                  <div style={styles.chatArea}>
                    <div style={styles.bubble}>
                      {renderPreview(text, previewContact) || "Sua mensagem aparecerá aqui..."}
                      <div style={styles.bubbleTime}>agora</div>
                    </div>
                  </div>
                </div>

                {parsed.valid.length > 0 && (
                  <Select
                    value={previewIndex}
                    onChange={setPreviewIndex}
                    style={{ width: "100%", marginTop: 12 }}
                    options={parsed.valid.slice(0, 30).map((item, index) => ({
                      value: index,
                      label: `${item.nome || item.name || "Sem nome"} — ${item.phone}`,
                    }))}
                  />
                )}
              </Card>

              <Card style={styles.card} title="Validação da lista">
                {contacts.length ? (
                  <>
                    <Row gutter={[12, 12]}>
                      <Col span={12}>
                        <Statistic title="Importados" value={contacts.length} />
                      </Col>
                      <Col span={12}>
                        <Statistic title="Válidos" value={parsed.valid.length} />
                      </Col>
                      <Col span={12}>
                        <Statistic title="Inválidos" value={parsed.invalid.length} />
                      </Col>
                      <Col span={12}>
                        <Statistic title="Duplicados" value={parsed.duplicated.length} />
                      </Col>
                    </Row>

                    <div style={{ height: 220, marginTop: 16 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={validationPie} dataKey="value" nameKey="name" outerRadius={80} label>
                            {validationPie.map((_, index) => (
                              <Cell key={index} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </>
                ) : (
                  <Empty description="Suba um Excel para validar os contatos" />
                )}
              </Card>
            </Space>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <Space style={styles.header}>
        <div>
          <h1 style={styles.title}>Broadcast Center</h1>
          <p style={styles.subtitle}>
            Gerencie campanhas de WhatsApp com Excel, variáveis e envio em lotes no n8n.
          </p>
        </div>

        <Space>
          <Button icon={<ReloadOutlined />} loading={loading} onClick={loadCampaigns}>
            Atualizar
          </Button>

          <Button type="primary" icon={<PlusOutlined />} size="large" onClick={() => setMode("create")}>
            Nova campanha
          </Button>
        </Space>
      </Space>

      <Row gutter={[16, 16]} style={{ marginBottom: 18 }}>
        <Col xs={24} md={6}>
          <Card style={styles.statCard}>
            <Statistic title="Campanhas" value={campaigns.length} />
          </Card>
        </Col>

        <Col xs={24} md={6}>
          <Card style={styles.statCard}>
            <Statistic title="Enviadas" value={totals.sent} />
          </Card>
        </Col>

        <Col xs={24} md={6}>
          <Card style={styles.statCard}>
            <Statistic title="Pendentes" value={totals.pending} />
          </Card>
        </Col>

        <Col xs={24} md={6}>
          <Card style={styles.statCard}>
            <Statistic title="Taxa sucesso" value={totals.successRate} suffix="%" />
          </Card>
        </Col>
      </Row>

      <Card style={styles.card} title="Resumo das campanhas">
        {campaigns.length ? (
          <div style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="enviados" />
                <Bar dataKey="falhas" />
                <Bar dataKey="pendentes" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <Empty description="Nenhuma campanha criada ainda" />
        )}
      </Card>

      <div style={styles.sectionTitle}>Campanhas</div>

      {campaigns.length ? (
        <Row gutter={[16, 16]}>
          {campaigns.map((campaign) => {
            const total = Number(campaign.totalValid || 0);
            const sent = Number(campaign.sentCount || 0);
            const failed = Number(campaign.failedCount || 0);
            const processed = sent + failed;
            const progress = percent(processed, total);

            return (
              <Col xs={24} md={12} xl={8} key={campaign.id}>
                <Card style={styles.campaignCard}>
                  <Space direction="vertical" size={12} style={{ width: "100%" }}>
                    <Space style={styles.cardHeader}>
                      <div>
                        <h3 style={styles.campaignTitle}>{campaign.name || "Campanha sem nome"}</h3>
                        <span style={styles.muted}>
                          {campaign.createdAt
                            ? new Date(campaign.createdAt).toLocaleString("pt-BR")
                            : "-"}
                        </span>
                      </div>

                      <Tag color={statusColor(campaign.status)}>{statusLabel(campaign.status)}</Tag>
                    </Space>

                    <Progress
                      percent={progress}
                      status={campaign.status === "DONE" ? "success" : "active"}
                    />

                    <div style={styles.progressText}>
                      {processed} de {total} processados
                    </div>

                    <Row gutter={[12, 12]}>
                      <Col span={8}>
                        <Statistic title="Total" value={total} />
                      </Col>
                      <Col span={8}>
                        <Statistic title="Enviados" value={sent} />
                      </Col>
                      <Col span={8}>
                        <Statistic title="Falhas" value={failed} />
                      </Col>
                    </Row>

                    <div style={styles.miniFooter}>
                      <span>⏳ Pendentes: {campaign.pendingCount || 0}</span>
                      <span>📌 Claimed: {campaign.claimedCount || 0}</span>
                    </div>
                  </Space>
                </Card>
              </Col>
            );
          })}
        </Row>
      ) : (
        <Card style={styles.card}>
          <Empty description="Nenhuma campanha criada ainda" image={Empty.PRESENTED_IMAGE_SIMPLE}>
            <Button type="primary" onClick={() => setMode("create")}>
              Criar primeira campanha
            </Button>
          </Empty>
        </Card>
      )}
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    padding: 18,
    background: "linear-gradient(180deg, #0b0b0c, #060607)",
    color: "white",
  },
  header: {
    width: "100%",
    justifyContent: "space-between",
    marginBottom: 18,
  },
  topBar: {
    width: "100%",
    justifyContent: "flex-start",
    marginBottom: 18,
  },
  title: {
    color: "white",
    margin: 0,
    fontSize: 34,
    fontWeight: 800,
    letterSpacing: "-1px",
  },
  subtitle: {
    color: "#9b9b9b",
    margin: 0,
    fontSize: 15,
  },
  card: {
    borderRadius: 16,
    border: "1px solid rgba(255,255,255,0.08)",
  },
  statCard: {
    borderRadius: 16,
    minHeight: 112,
  },
  sectionTitle: {
    color: "white",
    fontSize: 20,
    fontWeight: 700,
    margin: "22px 0 12px",
  },
  campaignCard: {
    borderRadius: 18,
    border: "1px solid rgba(255,255,255,0.08)",
  },
  cardHeader: {
    width: "100%",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  campaignTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 800,
  },
  muted: {
    color: "#999",
    fontSize: 12,
  },
  miniFooter: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    color: "#777",
    fontSize: 12,
  },
  progressText: {
    color: "#777",
    fontSize: 12,
    marginTop: -8,
  },
  label: {
    display: "block",
    marginBottom: 8,
    fontWeight: 700,
  },
  hint: {
    color: "#888",
    marginTop: 8,
    marginBottom: 0,
    fontSize: 12,
  },
  phoneMock: {
    background: "#111",
    borderRadius: 22,
    padding: 14,
    border: "1px solid rgba(255,255,255,0.08)",
  },
  phoneHeader: {
    color: "white",
    fontWeight: 700,
    padding: "10px 12px",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
  },
  chatArea: {
    minHeight: 240,
    padding: 16,
    background:
      "radial-gradient(circle at top left, rgba(37,211,102,.12), transparent 35%), #0b141a",
    borderRadius: 16,
    marginTop: 12,
  },
  bubble: {
    background: "#dcf8c6",
    color: "#111",
    padding: "12px 14px",
    borderRadius: "14px 14px 4px 14px",
    maxWidth: "85%",
    marginLeft: "auto",
    whiteSpace: "pre-wrap",
    lineHeight: 1.45,
  },
  bubbleTime: {
    textAlign: "right",
    color: "#555",
    fontSize: 11,
    marginTop: 6,
  },
};